import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import BrandMark from './BrandMark';
import ForgeIcon from './ForgeIcon';
import StatusPill from './StatusPill';
import { API_URL } from '../api';
import { useAuth } from '../context/AuthContext';
import '../styles/forge.css';

const navItems = [
  { key: 'home', label: 'Empezar', path: '/dashboard', icon: 'home' },
  { key: 'learn', label: 'Aprender', path: '/learn', icon: 'learn', n: '01' },
  { key: 'readiness', label: 'Preparación', path: '/readiness', icon: 'users' },
  { key: 'govern', label: 'Gobernar', path: '/govern', icon: 'shield', n: '02' },
  { key: 'observe', label: 'Observar', path: '/observe', icon: 'pulse', n: '03' },
  { key: 'work', label: 'Trabajar', path: '/work', icon: 'board', n: '04' },
  { key: 'secure', label: 'Asegurar', path: '/secure', icon: 'lock', n: '05' },
];

const adminNavItem = { key: 'admin', label: 'Administrar', path: '/admin', icon: 'admin', n: '06' };

const roles = {
  FORGE_MASTER: {
    label: 'Forge Master',
    initials: 'FM',
    mode: 'admin',
    hint: 'Gobierno, métricas y excepciones.',
  },
  CONTEXT_OWNER: {
    label: 'Context Owner',
    initials: 'CO',
    mode: 'producto',
    hint: 'Intención, specs y criterios.',
  },
  ARCHITECT: {
    label: 'Architect',
    initials: 'AR',
    mode: 'producto',
    hint: 'Arquitectura, decisiones y mapas.',
  },
  BUILDER: {
    label: 'Builder',
    initials: 'BU',
    mode: 'producto',
    hint: 'Specs, código, PRs y tests.',
  },
  QUALITY_GUARDIAN: {
    label: 'Quality Guardian',
    initials: 'QG',
    mode: 'producto',
    hint: 'Evals, gates y semáforo.',
  },
  PLATFORM_ENGINEER: {
    label: 'Platform Engineer',
    initials: 'PE',
    mode: 'producto',
    hint: 'Infraestructura, CI y runtime.',
  },
  EXECUTIVE_SPONSOR: {
    label: 'Executive / Sponsor',
    initials: 'ES',
    mode: 'producto',
    hint: 'Prioridades, valor y riesgo.',
  },
  SUPPORT: {
    label: 'Soporte',
    initials: 'SP',
    mode: 'admin',
    hint: 'Intervención y asistencia operativa.',
  },
};

function activeKeyFor(pathname) {
  if (pathname.startsWith('/learn')) return 'learn';
  if (pathname.startsWith('/readiness')) return 'readiness';
  if (pathname.startsWith('/govern')) return 'govern';
  if (pathname.startsWith('/observe')) return 'observe';
  if (pathname.startsWith('/work') || pathname.startsWith('/specs') || pathname.startsWith('/board') || pathname.startsWith('/project-spine') || pathname.startsWith('/execution')) return 'work';
  if (pathname.startsWith('/secure')) return 'secure';
  if (pathname.startsWith('/admin')) return 'admin';
  return 'home';
}

function roleFor(value) {
  return roles[value] || roles.CONTEXT_OWNER;
}

function ForgeShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user: authUser, logout: logoutSession } = useAuth();
  const userId = searchParams.get('user_id') || (authUser?.id ? String(authUser.id) : null);
  const username = searchParams.get('username') || authUser?.username;
  const repoId = searchParams.get('repo_id');
  const [overview, setOverview] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [brand, setBrand] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [rolesOpen, setRolesOpen] = useState(false);
  const rolesRef = useRef(null);

  const activeKey = activeKeyFor(location.pathname);
  const currentRoles = currentUser?.roles || [];
  const visibleNavItems = useMemo(
    () => (currentUser?.can_admin ? [...navItems, adminNavItem] : navItems),
    [currentUser?.can_admin]
  );

  const query = useMemo(() => {
    const params = new URLSearchParams();
    if (userId) params.set('user_id', userId);
    if (username) params.set('username', username);
    if (repoId) params.set('repo_id', repoId);
    const value = params.toString();
    return value ? `?${value}` : '';
  }, [repoId, userId, username]);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    fetch(`${API_URL}/api/overview?user_id=${userId}`, { credentials: 'include' })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!cancelled) setOverview(data);
      })
      .catch(() => {
        if (!cancelled) setOverview(null);
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_URL}/api/admin/brand`, { credentials: 'include' })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!cancelled) setBrand(data);
      })
      .catch(() => {
        if (!cancelled) setBrand(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!userId) {
      setCurrentUser(null);
      return;
    }
    if (authUser) {
      setCurrentUser(authUser);
    }
    let cancelled = false;
    fetch(`${API_URL}/api/auth/me?user_id=${userId}`, { credentials: 'include' })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!cancelled) setCurrentUser(data);
      })
      .catch(() => {
        if (!cancelled) setCurrentUser(null);
      });
    return () => {
      cancelled = true;
    };
  }, [authUser, userId]);

  useEffect(() => {
    if (!brand) return;
    const root = document.documentElement;
    root.style.setProperty('--forge-cyan', brand.primary_color || '#00d4ff');
    root.style.setProperty('--forge-mint', brand.accent_color || '#00ff9f');

    let favicon = document.querySelector("link[rel='icon']");
    if (!favicon) {
      favicon = document.createElement('link');
      favicon.rel = 'icon';
      document.head.appendChild(favicon);
    }
    favicon.href = brand.favicon_url || '/favicon.ico';
    document.title = brand.brand_name || 'Forge';
  }, [brand]);

  useEffect(() => {
    setRolesOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!rolesOpen) return undefined;
    const closeOnOutsidePointer = (event) => {
      if (!rolesRef.current?.contains(event.target)) setRolesOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setRolesOpen(false);
    };
    document.addEventListener('pointerdown', closeOnOutsidePointer);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOnOutsidePointer);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [rolesOpen]);

  const crumb = location.pathname.startsWith('/profile')
    ? 'Perfil'
    : visibleNavItems.find((item) => item.key === activeKey)?.label || 'Forge';

  const handleLogout = async () => {
    await logoutSession();
    window.localStorage.removeItem('forge:user');
    window.sessionStorage.removeItem('forge:user');
    setOverview(null);
    setMenuOpen(false);
    navigate('/');
  };

  return (
    <div className="forge-shell">
      <button
        type="button"
        className={`forge-scrim ${menuOpen ? 'open' : ''}`}
        aria-label="Cerrar menú"
        onClick={() => setMenuOpen(false)}
      />

      <aside className={`forge-side ${menuOpen ? 'open' : ''}`}>
        <Link to={`/dashboard${query}`} className="forge-brand-link" onClick={() => setMenuOpen(false)}>
          <BrandMark brand={brand} />
        </Link>

        <nav className="forge-nav" aria-label="Plataforma">
          <div className="forge-nav-eyebrow">Plataforma</div>
          {visibleNavItems.map((item) => (
            <Link
              key={item.key}
              to={`${item.path}${query}`}
              className={`forge-nav-item ${activeKey === item.key ? 'active' : ''}`}
              onClick={() => setMenuOpen(false)}
            >
              {item.n && <span className="forge-nav-number">{item.n}</span>}
              <ForgeIcon name={item.icon} />
              <span>{item.label}</span>
              {activeKey === item.key && <span className="forge-reco" />}
            </Link>
          ))}
        </nav>

        <div className="forge-side-foot">
          <div className="forge-side-tag">ISO/IEC 42001 · v1</div>
          <div className="forge-side-tag accent">AWS-first · portable</div>
        </div>
      </aside>

      <div className="forge-main">
        <header className="forge-top">
          <button type="button" className="forge-menu-btn" onClick={() => setMenuOpen(true)}>
            <ForgeIcon name="menu" />
            <span className="sr-only">Abrir menú</span>
          </button>

          <div className="forge-crumb">
            <span>Forge</span>
            <span className="sep">/</span>
            <b>{crumb}</b>
          </div>

          <div className="forge-top-right">
            {overview?.repos?.total > 0 && (
              <span className="forge-repo-chip" title="Repositorios en Forge">
                <ForgeIcon name="github" />
                {overview.repos.total} repo{overview.repos.total === 1 ? '' : 's'}
              </span>
            )}

            <div className="forge-sem-summary" title="Semáforo agregado">
              <span><span className="sd green" />{overview?.sem_summary?.green ?? 0}</span>
              <span><span className="sd amber" />{overview?.sem_summary?.amber ?? 0}</span>
              <span><span className="sd red" />{overview?.sem_summary?.red ?? 0}</span>
            </div>

            <div className="forge-role-wrap" ref={rolesRef}>
              <button
                type="button"
                className="forge-role-summary"
                title="Ver roles actuales"
                aria-label={`Ver roles actuales: ${currentRoles.join(', ') || 'sin roles'}`}
                aria-expanded={rolesOpen}
                aria-controls="forge-role-popover"
                aria-haspopup="dialog"
                onClick={() => setRolesOpen((open) => !open)}
              >
                <span className="forge-role-summary-avatars" aria-hidden="true">
                  {currentRoles.slice(0, 3).map((roleValue) => {
                    const role = roleFor(roleValue);
                    return <span className="forge-role-av" key={roleValue}>{role.initials}</span>;
                  })}
                </span>
                <span className="forge-role-summary-label">{currentRoles.length || 0} roles</span>
              </button>
              {rolesOpen && <section id="forge-role-popover" className="forge-role-popover" role="dialog" aria-label="Roles actuales">
                <div className="forge-role-popover-head"><strong>Roles actuales</strong><span>{currentRoles.length || 0} activos</span></div>
                <div className="forge-role-popover-list">
                  {currentRoles.map((roleValue) => {
                    const role = roleFor(roleValue);
                    return <article key={roleValue}><span className="forge-role-av">{role.initials}</span><div><b>{role.label}</b><small>{role.hint}</small></div></article>;
                  })}
                  {!currentRoles.length && <p>No hay roles asignados todavía.</p>}
                </div>
                <Link to={`/profile${query}`} className="forge-role-popover-link" onClick={() => setRolesOpen(false)}>Ver perfil completo</Link>
              </section>}
            </div>

            {userId && (
              <button type="button" className="forge-logout-btn" onClick={handleLogout} title="Cerrar sesión">
                <ForgeIcon name="logout" />
                <span>Salir</span>
              </button>
            )}
          </div>
        </header>

        <main className="forge-content">
          {!userId && location.pathname !== '/' && (
            <div className="forge-inline-alert">
              <StatusPill color="amber">Sin sesión</StatusPill>
              <span>Conecta GitHub para ver datos reales de repositorios, specs y agentes.</span>
            </div>
          )}
          <Outlet context={{ overview, apiUrl: API_URL, query, userId, username, currentUser, brand }} />
        </main>
      </div>
    </div>
  );
}

export default ForgeShell;
